/* eslint-disable @typescript-eslint/no-explicit-any */
import {
 Autocomplete,
 Box,
 Button,
 Chip,
 FormControl,
 InputLabel,
 MenuItem,
 Select,
 TextField,
 Typography,
} from '@mui/material';
import React from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
 getGroupOptions,
 getLanguageOptions,
 getNotificationOptions,
 getRepresentingOptions,
} from './utils';
import { UserTabProps } from './types';
import UserAvatar from '@components/common/Header/UserBadge';
import TwoFactorSection from './TwoFactorSection';
import Verify2FA from '@components/login/Verify2FA';

interface UserTabExtendedProps extends UserTabProps {
 twoFactorEnabled: boolean;
 qrCode: string | null;
 verificationCode: string;
 onGenerateQR: () => void;
 onVerifyTotp: () => void;
 onVerificationCodeChange: (code: string) => void;
 onTwoFactorChange: (enabled: boolean) => void;
 setQrCode: (code: string | null) => void;
 twoFactorAuthMethod: 1 | 2 | null;
 setTwoFactorAuthMethod: (method: 1 | 2 | null) => void;
 onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
}

const UserTab: React.FC<UserTabExtendedProps> = ({
 control,
 errors,
 watch,
 user,
 groups,
 loading,
 on2FACodeSubmit,
 onSubmit,
 i18n,
 twoFactorEnabled,
 qrCode,
 verificationCode,
 onGenerateQR,
 onVerifyTotp,
 onVerificationCodeChange,
 onTwoFactorChange,
 setQrCode,
 twoFactorAuthMethod,
 setTwoFactorAuthMethod,
}) => {
 const { t } = useTranslation();
 const languageOptions = getLanguageOptions(t);
 const representingOptions = getRepresentingOptions(user);
 const groupOptions = getGroupOptions(groups);
 const notificationOptions = getNotificationOptions(t);

 return (
  <form id="user-profile-form" onSubmit={onSubmit}>
   <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    <UserAvatar user={user} isHorizontal={true} />

    <Box>
     <Typography
      sx={{
       fontSize: '14px',
       color: (th) => th.palette.kemek.darkGray,
       fontWeight: 500,
       mb: 2,
      }}
     >
      {t('user.settings')}
     </Typography>

     <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <FormControl fullWidth error={!!errors.language}>
       <InputLabel id="language-label">{t('user.fields.language')}</InputLabel>
       <Controller
        name="language"
        control={control}
        rules={{
         required: t('user.errors.required', {
          field: t('user.fields.language'),
         }),
        }}
        render={({ field }) => (
         <Select
          {...field}
          labelId="language-label"
          id="language"
          label={t('user.fields.language')}
          sx={{ backgroundColor: (th) => th.palette.background.paper }}
          onChange={(e) => {
           field.onChange(e.target.value);
           i18n.changeLanguage(e.target.value);
          }}
         >
          {languageOptions.map((option) => (
           <MenuItem key={option.value} value={option.value}>
            {option.label}
           </MenuItem>
          ))}
         </Select>
        )}
       />
      </FormControl>
      {['email', 'phone'].map((fieldName: string) => (
       <Box key={fieldName}>
        <Box sx={{ display: 'flex', gap: 1 }}>
         <FormControl fullWidth error={!!errors[fieldName]}>
          <Controller
           name={fieldName}
           control={control}
           rules={{
            required: t('user.errors.required', {
             field: t(`user.fields.${fieldName}`),
            }),
            pattern:
             fieldName === 'email'
              ? {
                 value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                 message: t('user.errors.invalidEmail'),
                }
              : {
                 value: /^\+3706\d{7}$/,
                 message: t('user.errors.invalidPhone'),
                },
           }}
           render={({ field }) => (
            <TextField
             {...field}
             id={fieldName}
             type={fieldName === 'email' ? 'email' : 'tel'}
             label={t(`user.fields.${fieldName}`)}
             variant="outlined"
             error={!!errors[fieldName]}
             helperText={errors[fieldName]?.message}
             sx={{ backgroundColor: (th) => th.palette.background.paper }}
            />
           )}
          />
         </FormControl>
         {user?.[fieldName.charAt(0).toUpperCase() + fieldName.slice(1)] && (
          <Button
           variant="outlined"
           size="medium"
           sx={{ minHeight: 44 }}
           onClick={() => {
            setTwoFactorAuthMethod(fieldName === 'email' ? 1 : 2);
           }}
           disabled={
            user?.[
             fieldName.charAt(0).toUpperCase() +
              fieldName.slice(1) +
              'Confirmed'
            ]
           }
          >
           {t('user.buttons.confirm')}
          </Button>
         )}
        </Box>
        {twoFactorAuthMethod === (fieldName === 'email' ? 1 : 2) && (
         <Verify2FA
          verificationCode={verificationCode}
          onVerificationCodeChange={onVerificationCodeChange}
          onVerifyTotp={onVerifyTotp}
          loading={loading}
          method={twoFactorAuthMethod}
          control={control}
          setTwoFactorAuthMethod={setTwoFactorAuthMethod}
         />
        )}
       </Box>
      ))}

      <FormControl fullWidth error={!!errors.representing}>
       <InputLabel id="representing-label">
        {t('user.fields.representing')}
       </InputLabel>
       <Controller
        name="representing"
        control={control}
        render={({ field }) => (
         <Select
          {...field}
          labelId="representing-label"
          id="representing"
          label={t('user.fields.representing')}
          sx={{ backgroundColor: (th) => th.palette.background.paper }}
         >
          {representingOptions.map((option) => (
           <MenuItem key={option.value} value={option.value}>
            {option.label}
           </MenuItem>
          ))}
         </Select>
        )}
       />
      </FormControl>

      <FormControl fullWidth error={!!errors.group}>
       <Controller
        name="group"
        control={control}
        render={({ field }) => (
         <Autocomplete
          {...field}
          multiple
          options={groupOptions}
          value={field.value || []}
          onChange={(_, value) => field.onChange(value)}
          renderTags={(value, getTagProps) =>
           value.map((option, index) => (
            <Chip
             {...getTagProps({ index })}
             key={option}
             label={option}
             onDelete={() => {
              const newValue = field.value.filter(
               (_: any, i: number) => i !== index,
              );
              field.onChange(newValue);
             }}
            />
           ))
          }
          renderInput={(params) => (
           <TextField
            {...params}
            label={t('user.fields.group')}
            variant="outlined"
            sx={{ backgroundColor: (th) => th.palette.background.paper }}
           />
          )}
         />
        )}
       />
      </FormControl>

      <FormControl fullWidth error={!!errors.notificationMethod}>
       <Controller
        name="notificationMethod"
        control={control}
        render={({ field }) => (
         <Autocomplete
          {...field}
          multiple
          options={notificationOptions}
          value={field.value || []}
          onChange={(_, value) => field.onChange(value)}
          renderTags={(value, getTagProps) =>
           value.map((option, index) => (
            <Chip
             {...getTagProps({ index })}
             key={option}
             label={option}
             onDelete={() => {
              const newValue = field.value.filter(
               (_: any, i: number) => i !== index,
              );
              field.onChange(newValue);
             }}
            />
           ))
          }
          renderInput={(params) => (
           <TextField
            {...params}
            label={t('user.fields.notificationMethod')}
            variant="outlined"
            sx={{ backgroundColor: (th) => th.palette.background.paper }}
           />
          )}
         />
        )}
       />
      </FormControl>
     </Box>
    </Box>

    <TwoFactorSection
     control={control}
     twoFactorEnabled={twoFactorEnabled}
     qrCode={qrCode}
     loading={loading}
     onGenerateQR={onGenerateQR}
     onTwoFactorChange={onTwoFactorChange}
     setQrCode={setQrCode}
    />
   </Box>
  </form>
 );
};

export default UserTab;

